import React, { useState, useEffect, useCallback, useRef } from 'react';

/**
 * MoneyInput — input de moeda BRL com formatação em tempo real.
 *
 * Problemas do <input type="number"> nativo que este componente resolve:
 *   - Mantém "0" fixo na frente quando usuário tenta limpar
 *   - Não formata vírgula decimal enquanto digita
 *   - Não mostra "R$" junto do valor
 *   - Em mobile abre teclado numérico sem suporte a ponto/vírgula confiável
 *
 * Abordagem:
 *   - State interno guarda apenas os DÍGITOS digitados (ex: "4083")
 *   - Formatação visual "R$ 40,83" é derivada a cada render
 *   - Primeiro dígito digitado substitui o zero implícito
 *   - Backspace remove o último dígito (UX esperada em apps de banco)
 *
 * API:
 *   <MoneyInput
 *     value={50.4}              // number (reais), não centavos
 *     onChange={v => setX(v)}   // callback recebe number em reais
 *     min={0}                   // opcional
 *     max={999999.99}           // opcional
 *     placeholder="R$ 0,00"     // opcional
 *     disabled={false}          // opcional
 *   />
 *
 * Valor externo e onChange são sempre em REAIS (float com 2 decimais).
 * Ex: R$ 40,83 → value=40.83, onChange recebe 40.83.
 */

// Formata centavos (int) em string "R$ X.XXX,XX"
function formatBRL(cents) {
  const total = Math.max(0, Math.round(cents || 0));
  const reais = Math.floor(total / 100);
  const centavos = String(total % 100).padStart(2, '0');
  // Grupos de milhar com ponto: 12345 → "12.345"
  const reaisStr = String(reais).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `R$ ${reaisStr},${centavos}`;
}

// Extrai apenas dígitos do input. "R$ 40,83" → "4083"
function onlyDigits(str) {
  return String(str || '').replace(/\D/g, '');
}

export default function MoneyInput({
  value,
  onChange,
  min,
  max,
  placeholder = 'R$ 0,00',
  disabled = false,
  autoFocus = false,
  name,
  id,
  required = false,
  className = '',
  style,
  ariaLabel,
}) {
  // Centavos como STRING permite distinguir "vazio" ("") de "zero" ("0").
  // Vazio mostra placeholder; zero mostra "R$ 0,00".
  const [rawCents, setRawCents] = useState(() => {
    if (value === null || value === undefined || value === '') return '';
    const n = Number(value);
    if (Number.isNaN(n) || n < 0) return '';
    return String(Math.round(n * 100));
  });

  // Sincroniza com value externo quando PAI muda (reset de form, fetch inicial).
  // Usa ref pra comparar com o último `value` externo visto — sem precisar
  // listar `rawCents` na lista de deps (o que causaria loop quando o próprio
  // onChange do input muda o state interno).
  const lastExternalRef = useRef(value);
  useEffect(() => {
    const ext = value === null || value === undefined || value === '' ? null : Number(value);
    const last = lastExternalRef.current === null || lastExternalRef.current === undefined || lastExternalRef.current === ''
      ? null : Number(lastExternalRef.current);
    // Se value externo não mudou desde a última vez, ignora — é provavelmente
    // um re-render sem alteração real do pai.
    if (ext === last) return;
    lastExternalRef.current = value;
    setRawCents(ext === null || Number.isNaN(ext) ? '' : String(Math.round(ext * 100)));
  }, [value]);

  const emit = useCallback((cents) => {
    if (!onChange) return;
    const reais = cents === '' ? 0 : Number(cents) / 100;
    onChange(reais);
  }, [onChange]);

  const handleChange = (e) => {
    if (disabled) return;
    const newValueRaw = e.target.value;
    const newDigits = onlyDigits(newValueRaw).slice(0, 11); // max ~R$ 999.999.999,99

    // Deteção de backspace sobre prefixo "R$ " ou separadores (",", "."):
    // Quando display é "R$ 0,00" e usuário aperta backspace, o browser
    // remove só o último char (dígito), mas se o dígito removido for um
    // "0" que já seria descartado pela normalização de zeros à esquerda,
    // o estado não mudava — usuário ficava travado no "R$ 0,00". Fix:
    // se dígitos ficaram IGUAIS mas o raw value encurtou (user removeu
    // algo visual), corta último dígito manualmente.
    const prevDisplay = rawCents === '' ? '' : formatBRL(Number(rawCents));
    if (newDigits === rawCents && newValueRaw.length < prevDisplay.length) {
      const trimmed = rawCents.slice(0, -1);
      setRawCents(trimmed);
      emit(trimmed);
      return;
    }

    // Caso normal: remove zeros à esquerda; se ficou vazio, permite vazio
    // (mostra placeholder). Antes a gente forçava "0" quando tinha algum
    // dígito, mas isso impedia o usuário de apagar até o final.
    const trimmed = newDigits.replace(/^0+/, '');
    setRawCents(trimmed);
    emit(trimmed);
  };

  const handleBlur = () => {
    // Ao sair do campo, aplica min/max se configurados
    if (rawCents === '') return;
    let cents = Number(rawCents);
    if (typeof min === 'number') {
      const minCents = Math.round(min * 100);
      if (cents < minCents) cents = minCents;
    }
    if (typeof max === 'number') {
      const maxCents = Math.round(max * 100);
      if (cents > maxCents) cents = maxCents;
    }
    const newRaw = String(cents);
    if (newRaw !== rawCents) {
      setRawCents(newRaw);
      emit(newRaw);
    }
  };

  const display = rawCents === '' ? '' : formatBRL(Number(rawCents));

  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      name={name}
      id={id}
      required={required}
      disabled={disabled}
      autoFocus={autoFocus}
      placeholder={placeholder}
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      className={className}
      style={style}
      aria-label={ariaLabel}
    />
  );
}

/**
 * formatMoneyBRL — helper compartilhado pra usar em spans de leitura.
 * Formato sempre "R$ X.XXX,XX" (inclusive pra inteiros: "R$ 49,00" em vez de "R$ 49").
 *
 * Uso:
 *   import { formatMoneyBRL } from '../components/MoneyInput';
 *   <span>{formatMoneyBRL(svc.price)}</span>
 */
export function formatMoneyBRL(value) {
  if (value === null || value === undefined || value === '') return 'R$ 0,00';
  const n = Number(value);
  if (Number.isNaN(n)) return 'R$ 0,00';
  return formatBRL(Math.round(n * 100));
}
